import React, { useMemo } from 'react';
import TestItem from '@/app/features/reports/components/test-tile';
import { Assessment } from '@app.raytd.com/store';
import { FlattenedItem, ReportTestItem } from '@/app/features/reports/utils/entities';
import { TestSuite } from 'store/src/lib/tests/entity';
import { cn } from '@/lib/utils';

interface PrintableTileWrapperProps {
    item: ReportTestItem;
    parents: string[];
    header?: FlattenedItem;
    assessment?: Assessment;
    testSuite?: TestSuite;
    /**
     * SDA's compact single-row tile is routinely far shorter than a page, so forcing a page
     * break after every tile (the default, for every other Report Format) wastes most of a
     * landscape page per tile. SDA tiles flow naturally instead, keeping only
     * `break-inside: avoid` (.printable-tile-wrapper, print.css) so a tile itself never splits.
     */
    isSdaFormat?: boolean;
}

const RepeatingGroupHeader: React.FC<{ parents: string[]; header?: FlattenedItem }> = ({ parents, header }) => {
    const headerTitle = useMemo(() => {
        if (parents.length) return parents[parents.length - 1];
        if (header?.title) return header.title;
        return '';
    }, [parents, header?.title]);

    const hierarchy = useMemo(() => {
        if (parents.length <= 1) return '';
        return parents.slice(0, -1).join(', ');
    }, [parents]);

    const metrics = useMemo(() => {
        if (!header) return '';
        const parts: string[] = [];
        if (header.score) parts.push(`Score ${header.score}`);
        if (typeof header.includedCount === 'number') parts.push(`Included ${header.includedCount}`);
        if (typeof header.count === 'number') parts.push(`Total ${header.count}`);
        return parts.join(' • ');
    }, [header]);

    if (!headerTitle) return null;

    return (
        <div className="repeating-group-header py-2 print:px-2 break-inside-avoid bg-zinc-100 border-t border-t-zinc-400">
            {hierarchy && (
                <p className="text-base font-medium text-zinc-900 break-before-avoid break-after-avoid">
                    {hierarchy}
                </p>
            )}
            {/* Same size, weight and color as the category line above — QA 108.02 item 5
                flagged both the size mismatch (this used to be text-lg/5) and, in the retest,
                that the category line still read lighter than this one. */}
            <h2 className="text-base font-medium text-zinc-900 break-before-avoid break-after-avoid">
                {headerTitle}
            </h2>
        </div>
    );
};

const PrintableTileWrapperComponent: React.FC<PrintableTileWrapperProps> = ({
    item,
    parents,
    header,
    assessment,
    testSuite,
    isSdaFormat = false,
}) => {
    return (
        <div className={cn(
            'printable-tile-wrapper space-y-2 bg-white',
            !isSdaFormat && 'break-after-page'
        )}>
            <RepeatingGroupHeader parents={parents} header={header} />
            <TestItem
                item={item}
                assessment={assessment}
                testSuite={testSuite}
                isPrintView
            />
            <hr className="border-zinc-200" />
        </div>
    );
};

const PrintableTileWrapper = React.memo(PrintableTileWrapperComponent);

export default PrintableTileWrapper;

